import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "react-feather";
import logo from "../../assets/images/logo.jpg";
import MagneticButton from "./hero/MagneticButton";

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/rudra-rides", label: "Rudra Ride" },
  { to: "/edupravahaa", label: "Edu Pravahaa" },
  { to: "/services", label: "Services" },
  { to: "/launch-event", label: "Launch Recap" },
  { to: "/contact", label: "Contact" },
];

function NavItem({ to, label, end, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `relative px-3 py-2 text-[13px] font-medium tracking-wide transition-colors group ${
          isActive ? "text-white" : "text-white/65 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {label}
          <span
            className={`absolute -bottom-0.5 left-3 right-3 h-0.5 rounded-full bg-gradient-to-r from-[#2563EB] to-[#F5C044] transition-all duration-300 ${
              isActive ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100"
            }`}
          />
          <span className="absolute inset-0 rounded-lg bg-white/0 group-hover:bg-white/[0.04] transition-colors -z-10" />
        </>
      )}
    </NavLink>
  );
}

export default function PremiumNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const barRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // useEffect(() => {
  //   setMobileOpen(false);
  //   document.body.style.overflow = mobileOpen ? "hidden" : "";
  //   return () => {
  //     document.body.style.overflow = "";
  //   };
  // }, [mobileOpen, location.pathname]);

  // 1. Only run this when the page route changes (to close the menu after clicking a link)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // 2. Only run this to lock/unlock the body scroll when the menu opens/closes
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] px-3 sm:px-5 pt-3 sm:pt-4">
        <motion.div
          ref={barRef}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`max-w-[1400px] mx-auto rounded-2xl transition-all duration-500 premium-nav-glass ${
            scrolled ? "premium-nav-glass--scrolled" : ""
          }`}
        >
          {/* <div className="grid grid-cols-[1fr_auto_1fr] items-center h-14 lg:h-[4.25rem] px-3 sm:px-5 lg:px-6 gap-2"> */}
          {/* Replace it with this line: */}
           <div className="flex justify-between xl:grid xl:grid-cols-[1fr_auto_1fr] items-center w-full h-14 lg:h-[4.25rem] px-3 sm:px-5 lg:px-6 gap-2">
            {/* Left — Logo */}
            <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group justify-self-start min-w-0">
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-1 rounded-xl bg-[#2563EB]/30 blur-md opacity-60 group-hover:opacity-100 transition-opacity" />
                <img
                  src={logo}
                  alt="EPT Pravahaa"
                  className="relative h-12 w-12 sm:h-14 sm:w-14 rounded-xl object-cover ring-2 ring-white/20 group-hover:ring-[#F5C044]/50 transition-all"
                />
              </div>
              {/* <div className="hidden md:block min-w-0">
                <p className="text-white font-bold text-sm leading-tight tracking-wide truncate">
                  EPT PRAVAHAA
                </p>
                <p className="text-[9px] sm:text-[10px] text-[#F5C044]/90 uppercase tracking-[0.2em] font-semibold">
                  Edu Tech Solutions
                </p>
              </div> */}

              <div className="hidden md:block min-w-0 flex flex-col justify-center">
               <span 
                 className="block text-white font-bold text-sm tracking-wide truncate"
                 style={{ lineHeight: '1' }}
              >EPT PRAVAHAA</span>
              <span 
                    className="block text-[9px] sm:text-[10px] text-[#F5C044]/90 uppercase tracking-[0.2em] font-semibold"
                  style={{ lineHeight: '1', marginTop: '1px' }}
                >Edu Tech Solutions</span>
              </div>
            </Link>

            {/* Center — Desktop nav */}
            <nav className="hidden xl:flex items-center justify-center gap-0.5 justify-self-center">
              {navLinks.map((link) => (
                <NavItem key={link.to} {...link} onNavigate={closeMobile} />
              ))}
            </nav>

            {/* Right — CTA + mobile toggle */}
            <div className="flex items-center justify-end gap-2 sm:gap-3 justify-self-end">
              <div className="hidden lg:block">
                <MagneticButton to="/enroll" variant="gold" className="!px-5 !py-2.5 !text-sm !rounded-xl">
                  Enroll Now
                </MagneticButton>
              </div>
              <button
                type="button"
                className="xl:hidden p-2.5 rounded-xl text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
              >
                <motion.div animate={{ rotate: mobileOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
                  {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                </motion.div>
              </button>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[105] bg-black/60 backdrop-blur-sm xl:hidden"
              onClick={closeMobile}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="fixed top-0 right-0 bottom-0 z-[110] w-[min(100%,320px)] premium-nav-glass border-l border-white/10 xl:hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <span className="text-white font-bold">Menu</span>
                <button type="button" onClick={closeMobile} className="p-2 text-white/80 hover:text-white">
                  <X size={22} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <NavLink
                      to={link.to}
                      end={link.end}
                      onClick={closeMobile}
                      className={({ isActive }) =>
                        `block px-4 py-3.5 rounded-xl text-base font-medium transition-all ${
                          isActive
                            ? "text-white bg-white/10 border border-[#2563EB]/30"
                            : "text-white/75 hover:text-white hover:bg-white/5"
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="p-4 border-t border-white/10">
                <MagneticButton to="/enroll" variant="gold" className="w-full !justify-center">
                  Enroll Now
                </MagneticButton>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
